// src/components/recovery/ActivityCard.tsx
import React from "react";
import { FiCheck, FiEye } from "react-icons/fi";
import { createLogger } from "../../lib/logger";

const log = createLogger("ActivityCard");

interface ActivityCardProps {
  /** Activity title */
  title: string;
  /** Activity description */
  description: string;
  /** Color for the left stripe */
  stripeColor: string;
  /** Whether the activity is completed */
  isCompleted: boolean;
  /** Callback when toggled */
  onToggle: () => void;
  /** Whether the card is read-only (past week) */
  isReadOnly?: boolean;
}

/**
 * Card for weekly activities with left color stripe
 */
export const ActivityCard: React.FC<ActivityCardProps> = React.memo(({
  title,
  description,
  stripeColor,
  isCompleted,
  onToggle,
  isReadOnly = false,
}) => {
  return (
    <div className="bg-white rounded-2xl overflow-hidden relative">
      {/* Left color stripe */}
      <div 
        className="absolute left-0 top-0 bottom-0 w-[9px]"
        style={{ backgroundColor: stripeColor }}
      />

      {/* Content */}
      <div className="py-5 pr-5 pl-8 flex flex-col gap-4">
        {/* Title and description */}
        <div className="flex flex-col gap-1">
          <h3 className="m-0 text-xl font-semibold text-gray-900 font-dm-sans">
            {title}
          </h3>
          <p className="m-0 text-sm font-normal text-gray-900 font-dm-sans leading-snug">
            {description}
          </p>
        </div>

        {/* Checkbox row */}
        {isReadOnly ? (
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <FiEye size={16} />
           <span>{isCompleted ? "Done" : "Not done"}</span>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => { log.info("Activity toggled", { title, wasCompleted: isCompleted }); onToggle(); }}
            className="flex items-center gap-3 bg-transparent border-none p-0 cursor-pointer min-w-[120px]"
          >
            {/* Checkbox */}
            <div
              className={`w-4 h-4 rounded flex items-center justify-center transition-colors duration-200 ${
                isCompleted
                  ? "bg-brand-primary"
                  : "bg-white border border-brand-primary"
              }`}
            >
              {isCompleted && <FiCheck size={12} className="text-white" />}
            </div>

            {/* Label */}
            <span className="text-base font-normal text-gray-900 font-dm-sans">
              {isCompleted ? "Done" : "Mark as done"}
            </span>
          </button>
        )}
      </div>
    </div>
  );
});

ActivityCard.displayName = "ActivityCard";

export default ActivityCard;
